import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { ThemeContext } from "../../context/ThemeContext";

export const CarePlanItem = ({ id, careplanname, onClick, onDelete }) => {
  const navigate = useNavigate();
  const { user, token } = useAuthContext();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const { darkMode } = useContext(ThemeContext);
  const { enqueueSnackbar } = useSnackbar();

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/careplan/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/planphase/practiceid/${user?.practiceId}/careplanid/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/plantreatment/practiceid/${user?.practiceId}/careplanid/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await axios.post(
        `${process.env.REACT_APP_API_URL}/plantemplate/bulkdelete`,
        {
          practiceid: user?.practiceId,
          careplanid: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await axios.post(
        `${process.env.REACT_APP_API_URL}/planprotocol/bulkdelete`,
        {
          practiceid: user?.practiceId,
          careplanid: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await axios.post(
        `${process.env.REACT_APP_API_URL}/plancitation/bulkdelete`,
        {
          practiceid: user?.practiceId,
          careplanid: id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      enqueueSnackbar("Successfully Deleted!", { variant: "success" });
      onDelete(id);
    } catch (err) {
      enqueueSnackbar("Error Deleting careplan!", { variant: "error" });
      console.error("Error deleting careplan:", err);
    } finally {
      setOpenConfirmDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDialog(false);
  };

  const handleClicked = () => {
    onClick();
  };

  const handleModifyClicked = (e) => {
    e.stopPropagation();
    navigate(`/care-plan/${id}/modify`);
  };

  const handleCopyClicked = (e) => {
    e.stopPropagation();
    navigate(`/care-plan/${id}/copy`);
  };

  const handleDeleteClicked = (e) => {
    e.stopPropagation();
    setOpenConfirmDialog(true);
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minHeight: { xs: "120px", sm: "150px" },
          px: { xs: "20px", md: "28px" },
          py: { xs: "12px", sm: "14px" },
          background: getColors.primaryMain,
          borderRadius: "5px",
          cursor: "pointer",
          "&:hover": {
            background: getColors.listItemHoverBg,
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
        onClick={handleClicked}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            sx={{
              fontSize: { xs: "20px", sm: "22px" },
              color: getColors.buttonText,
              fontWeight: 600,
              textAlign: "left",
              whiteSpace: "normal",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {careplanname}
          </Typography>
        </Box>

        <Box display="flex" mt={1}>
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleModifyClicked}
          >
            Modify
          </Button>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{
              mx: { xs: 1, sm: "3px", md: 1 },
              bgcolor: "#CBE5FF",
            }}
          />
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleCopyClicked}
          >
            Copy
          </Button>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{
              mx: { xs: 1, sm: "3px", md: 1 },
              bgcolor: "#CBE5FF",
            }}
          />
          <Button
            variant="text"
            sx={{
              fontSize: { xs: "14px", sm: "15px", md: "16px" },
              color: "#CBE5FF",
              "&:hover": { background: "none", color: getColors.buttonText },
            }}
            onClick={handleDeleteClicked}
          >
            Delete
          </Button>
        </Box>
      </Box>
      <Dialog open={openConfirmDialog} onClose={handleCancelDelete}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this careplan?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pt: 0 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              color: darkMode
                ? getColors.appBarBgLight
                : getColors.appBarBgDark,
              textAlign: "center",
              "&: hover": {
                background: "none",
                color: getColors.primaryMain,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              borderRadius: "5px",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
